import { C, T } from "./config.js";
import { state } from "./state.js";

export const genId = () => Math.random().toString(36).slice(2, 11);

export const fmtCOP = n => new Intl.NumberFormat("es-CO",{style:"currency",currency:"COP",maximumFractionDigits:0}).format(Number(n)||0);

// Un PRECIO UNITARIO, con sus centavos cuando los tiene. fmtCOP redondea al
// peso, y en un precio eso engaña: la factura FVE66867 de STOCKSUR trae
// $4.323,53 por unidad y se veía «$4.324», así que 500 × $4.324 parecía no dar
// el subtotal de la factura. Los totales siguen en pesos enteros.
export const fmtPrecio = n => {
  const v = Number(n) || 0;
  const centavos = Math.abs(v - Math.round(v)) >= 0.005;
  return new Intl.NumberFormat("es-CO",{style:"currency",currency:"COP",minimumFractionDigits:centavos?2:0,maximumFractionDigits:centavos?2:0}).format(v);
};

export const formatMiles = n => new Intl.NumberFormat("es-CO",{maximumFractionDigits:0}).format(Number(n)||0);

export const fmtDate = d => { try { return new Date(d+"T12:00:00").toLocaleDateString("es-CO"); } catch { return d||""; } };

// Fecha de HOY en horario de Colombia (America/Bogota, UTC-5), en formato
// YYYY-MM-DD. La DIAN exige que la fecha de la factura coincida con la fecha
// de firma (hora Colombia), y la contabilidad debe llevar la misma fecha que
// vive el negocio. Usar toISOString() (UTC) de noche caía al día siguiente:
// la DIAN rechazaba (FAD09e) y los asientos de después de las 7 p.m. quedaban
// fechados "mañana". Por eso today() ES la fecha de Colombia — no hay una
// versión UTC a la mano para equivocarse.
export const todayCO = () => {
  const p = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Bogota", year: "numeric", month: "2-digit", day: "2-digit"
  }).format(new Date());
  return p; // en-CA da directamente YYYY-MM-DD
};

export const today = todayCO;

export const escapeHTML = s => String(s==null?"":s).replace(/[&<>"']/g, c => ({"&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;"}[c]));

export function parseNumber(value) {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return isNaN(value) ? 0 : value;
  let s = String(value).trim();
  if (!s) return 0;

  // Quitar símbolos de moneda y espacios
  s = s.replace(/[$\s]/g, "");
  const hasDot = s.includes(".");
  const hasComma = s.includes(",");

  if (hasDot && hasComma) {
    // Tiene ambos: el último carácter usado es el decimal
    if (s.lastIndexOf(".") > s.lastIndexOf(",")) {
      // "1,000.50" → punto es decimal
      s = s.replace(/,/g, "");
    } else {
      // "1.000,50" → coma es decimal
      s = s.replace(/\./g, "").replace(",", ".");
    }
  } else if (hasComma) {
    // Solo coma: decimal si tiene 1-2 dígitos después y solo una coma
    const parts = s.split(",");
    if (parts.length === 2 && parts[1].length <= 2) {
      s = parts[0] + "." + parts[1];
    } else {
      // Múltiples comas o más de 2 dígitos = separador de miles
      s = s.replace(/,/g, "");
    }
  } else if (hasDot) {
    // Solo punto: en Colombia es separador de miles si tiene 3 dígitos después
    // o si hay múltiples puntos. Decimal si tiene 1-2 dígitos después y un solo punto.
    const parts = s.split(".");
    if (parts.length === 2 && parts[1].length === 3 && /^\d+$/.test(parts[0]) && /^\d+$/.test(parts[1])) {
      // "1.000" → mil (separador de miles)
      s = s.replace(/\./g, "");
    } else if (parts.length > 2) {
      // "1.234.567" → varios puntos = separadores de miles
      s = s.replace(/\./g, "");
    }
    // Sino, "1.5" o "1.50" → punto decimal (dejar como está)
  }
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
}

// Porcentajes (retenciones, márgenes): NUNCA llevan separador de miles, así que
// no pueden pasar por parseNumber —que en formato colombiano lee "0.414" como
// 414 (punto = miles con tres dígitos detrás) y convertía un ReteICA del 0,414%
// en uno del 414%. Aquí el punto y la coma son siempre el decimal.
export function parsePct(value) {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return isNaN(value) ? 0 : value;
  const n = parseFloat(String(value).trim().replace(/[%\s]/g, "").replace(",", "."));
  return isNaN(n) ? 0 : n;
}

export function normalize(s) {
  return String(s||"").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

export function searchFilter(items, query, fields) {
  if (!query || !query.trim()) return items;
  const q = normalize(query);
  return items.filter(item =>
    fields.some(f => normalize(item[f]).includes(q))
  );
}

export function searchBox(id, value, placeholder, accentColor) {
  return `
    <div style="position:relative;margin-bottom:${T.sp[3]};">
      <span style="position:absolute;left:12px;top:50%;transform:translateY(-50%);font-size:14px;color:${C.muted};pointer-events:none;">🔍</span>
      <input
        id="${id}"
        type="text"
        value="${escapeHTML(value || "")}"
        placeholder="${placeholder}"
        style="${inputStyle}padding-left:32px;padding-right:${value ? "32px" : T.sp[3]};border-color:${value ? (accentColor || C.mathiz) : C.border};"
        data-search="${id}"
      />
      ${value ? `<button data-action="clear-search" data-target="${id}" style="position:absolute;right:8px;top:50%;transform:translateY(-50%);background:transparent;border:none;font-size:14px;color:${C.muted};cursor:pointer;padding:4px 6px;line-height:1;">✕</button>` : ""}
    </div>
  `;
}

export function sortArrow(tipo, field) {
  const cfg = (state.docSort && state.docSort[tipo]) || { field:"fecha", dir:"desc" };
  if (cfg.field !== field) return "";
  return cfg.dir === "asc" ? " ▲" : " ▼";
}

export function thSortable(tipo, field, label) {
  return `<th data-action="sort-docs" data-tipo="${tipo}" data-field="${field}" style="padding:${T.cell};text-align:left;font-size:${T.fs.sm};font-weight:700;color:${C.text};cursor:pointer;user-select:none;white-space:nowrap;" title="Ordenar por ${label}">${label}${sortArrow(tipo, field)}</th>`;
}

// Botón estándar del ERP. Densidad nivel 2 (Denso) tomada de los tokens T.
// Nota: antes el font-size se emitía sin unidad ("font-size:12;"), CSS inválido
// que hacía que los botones ignoraran el tamaño y heredaran el del navegador.
export const btnStyle = (color, variant, small) => {
  const base = `padding:${small?T.sp[1]+" "+T.sp[2]:T.sp[2]+" "+T.sp[4]};font-size:${small?T.fs.sm:T.fs.md};font-weight:600;border:none;border-radius:${T.r.md};cursor:pointer;display:inline-flex;align-items:center;gap:5px;transition:all .15s;font-family:inherit;line-height:1.35;`;
  if (variant === "secondary") return base + `background:#f1f5f9;color:${C.text};border:1px solid ${C.border};`;
  if (variant === "ghost") return base + `background:transparent;color:${C.text};`;
  return base + `background:${color||C.accent};color:white;`;
};

export const inputStyle = `padding:${T.sp[2]} ${T.sp[3]};border:1px solid ${C.border};border-radius:${T.r.md};font-size:${T.fs.md};color:${C.text};background:white;width:100%;box-sizing:border-box;outline:none;font-family:inherit;`;

export function addDias(fechaStr, dias) {
  try {
    const d = new Date(fechaStr + "T12:00:00");
    if (isNaN(d.getTime())) return null;
    d.setDate(d.getDate() + (parseInt(dias, 10) || 0));
    return d.toISOString().split("T")[0];
  } catch { return null; }
}

// Convierte un monto en pesos colombianos a letras, para el "SON:" de los
// documentos impresos. Ej: 1250000 → "UN MILLÓN DOSCIENTOS CINCUENTA MIL PESOS M/CTE"
const UNIDADES = ["", "UNO", "DOS", "TRES", "CUATRO", "CINCO", "SEIS", "SIETE", "OCHO", "NUEVE",
  "DIEZ", "ONCE", "DOCE", "TRECE", "CATORCE", "QUINCE", "DIECISÉIS", "DIECISIETE", "DIECIOCHO", "DIECINUEVE",
  "VEINTE", "VEINTIUNO", "VEINTIDÓS", "VEINTITRÉS", "VEINTICUATRO", "VEINTICINCO", "VEINTISÉIS", "VEINTISIETE", "VEINTIOCHO", "VEINTINUEVE"];
const DECENAS = ["", "", "", "TREINTA", "CUARENTA", "CINCUENTA", "SESENTA", "SETENTA", "OCHENTA", "NOVENTA"];
const CENTENAS = ["", "CIENTO", "DOSCIENTOS", "TRESCIENTOS", "CUATROCIENTOS", "QUINIENTOS", "SEISCIENTOS", "SETECIENTOS", "OCHOCIENTOS", "NOVECIENTOS"];

function tresDigitos(n) {
  if (n === 0) return "";
  if (n === 100) return "CIEN";
  const c = Math.floor(n / 100), resto = n % 100;
  let s = c ? CENTENAS[c] : "";
  if (resto) {
    if (s) s += " ";
    if (resto < 30) s += UNIDADES[resto];
    else {
      const d = Math.floor(resto / 10), u = resto % 10;
      s += DECENAS[d] + (u ? " Y " + UNIDADES[u] : "");
    }
  }
  return s;
}

function seisDigitos(n) {
  const miles = Math.floor(n / 1000), resto = n % 1000;
  let s = "";
  if (miles === 1) s = "MIL";
  else if (miles > 1) s = tresDigitos(miles).replace(/UNO$/, "UN") + " MIL";
  const r = tresDigitos(resto);
  return [s, r].filter(Boolean).join(" ");
}

export function montoEnLetras(monto) {
  const n = Math.round(Math.abs(Number(monto) || 0));
  if (n === 0) return "CERO PESOS M/CTE";
  const millones = Math.floor(n / 1e6), resto = n % 1e6;
  let s = "";
  if (millones === 1) s = "UN MILLÓN";
  else if (millones > 1) s = seisDigitos(millones).replace(/UNO$/, "UN") + " MILLONES";
  const r = seisDigitos(resto);
  const pesos = (millones > 0 && resto === 0) ? "DE PESOS" : "PESOS";
  return [s, r].filter(Boolean).join(" ") + " " + pesos + " M/CTE";
}

// ─── Traducción de errores de Supabase Auth ─────────────────────────────────
// Supabase devuelve sus mensajes en inglés y algunos llegan directo a la
// pantalla (política de contraseñas, correo repetido, límites de intentos).
// Se traducen por patrón; un mensaje no contemplado pasa tal cual, que es
// mejor que ocultarlo.
export function traducirErrorAuth(msg) {
  const m = String(msg || "");
  const partes = [];
  if (/Password should contain at least one character of each/i.test(m)) {
    partes.push("La contraseña debe incluir al menos una minúscula, una mayúscula, un número y un símbolo (por ejemplo * + - = ? ! # $ % @).");
  }
  if (/known to be weak and easy to guess/i.test(m)) {
    partes.push("Además, esa contraseña es de las más usadas del mundo y es fácil de adivinar: elige una distinta (el botón 🎲 Generar crea una válida).");
  }
  if (partes.length) return partes.join(" ");
  const mMin = m.match(/Password should be at least (\d+) characters/i);
  if (mMin) return `La contraseña debe tener al menos ${mMin[1]} caracteres.`;
  if (/already.*(registered|been registered)/i.test(m)) return "Ya existe un usuario registrado con ese correo.";
  if (/For security purposes, you can only request this after (\d+) seconds?/i.test(m)) {
    return "Por seguridad hay que esperar unos segundos antes de volver a intentarlo.";
  }
  if (/New password should be different/i.test(m)) return "La contraseña nueva debe ser distinta de la actual.";
  if (/Invalid login credentials/i.test(m)) return "Correo o contraseña incorrectos.";
  if (/AAL2 session is required|aal2.*required|insufficient.*aal/i.test(m)) {
    return "Por seguridad, para cambiar la contraseña primero debes confirmar con el código de verificación en dos pasos de tu app autenticadora.";
  }
  if (/Invalid TOTP code|invalid.*code|totp.*invalid/i.test(m)) return "El código de verificación es incorrecto o expiró. Revisa tu app autenticadora e inténtalo de nuevo.";
  if (/factor.*not found|no.*factor/i.test(m)) return "No se encontró tu verificación en dos pasos. Cierra sesión e inténtalo de nuevo.";
  return m;
}

// ─── Correos que se ven bien pero no reciben nada ────────────────────────────
// Una tilde o una eñe dentro de un correo casi siempre es un error de
// digitación, y el costo es alto y silencioso: la factura electrónica sale
// marcada como "enviada" y nunca llega, porque la dirección con caracteres
// fuera del ASCII necesita SMTPUTF8 y la mayoría de servidores no lo tiene.
// Nadie se entera hasta que el cliente reclama que no la recibió.
//
// Devuelve el motivo del problema, o null si el correo está bien. No corrige
// nada por su cuenta: cambiarle a alguien la dirección de facturación sin
// preguntarle sería peor que avisar.
export function problemaCorreo(correo) {
  const v = String(correo || "").trim();
  if (!v) return null;
  if (/\s/.test(v)) return "tiene espacios";
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) return "no tiene forma de correo (algo@dominio.com)";
  const raros = [...new Set([...v].filter(c => c.codePointAt(0) > 127))];
  if (raros.length) return `tiene ${raros.length === 1 ? "un carácter que no es" : "caracteres que no son"} del alfabeto inglés (${raros.join(" ")})`;
  return null;
}

// Cómo quedaría el mismo correo sin tildes ni eñes, para poder proponerlo.
export function correoSinTildes(correo) {
  return String(correo || "").trim()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/ñ/g, "n").replace(/Ñ/g, "N")
    .replace(/\s/g, "");
}

// ─── Días de pago de un tercero ─────────────────────────────────────────────
// `parseInt(x.dias_pago) || 30` parece inofensivo y no lo es: en JavaScript el
// 0 es falsy, así que un cliente DE CONTADO (dias_pago = 0) caía al valor por
// defecto y su factura salía a 30 días. Le pasó a la MP155 de SUMATEC, que se
// emitió a la DIAN diciendo "Crédito 30 días" con vencimiento a un mes cuando
// el cliente paga de contado.
//
// Aquí el 0 es un valor legítimo: solo se usa el defecto cuando el campo está
// vacío o no es un número.
export function diasPagoDe(tercero, porDefecto = 30) {
  const n = parseInt(tercero && tercero.dias_pago, 10);
  return Number.isFinite(n) && n >= 0 ? n : porDefecto;
}

// ─── Código interno de los productos ────────────────────────────────────────
// Secuencial de cinco dígitos con ceros a la izquierda ("00001"), en el orden en
// que se dieron de alta. Es TEXTO: guardarlo como número perdería los ceros y
// "00042" pasaría a ser 42.
export function formatearCodigoProducto(n) {
  return String(Math.max(1, Math.floor(Number(n) || 1))).padStart(5, "0");
}

// El siguiente libre, a partir de los que ya existen. Se busca el mayor y se
// avanza hasta encontrar uno que nadie tenga: si dos personas crean un producto
// casi a la vez, el segundo no se queda con el código del primero.
export function siguienteCodigoProducto(productos) {
  const usados = new Set((productos || []).map(p => String(p.codigo_interno || "").trim()).filter(Boolean));
  let n = 0;
  usados.forEach(c => { const v = parseInt(c, 10); if (Number.isFinite(v) && v > n) n = v; });
  let siguiente = formatearCodigoProducto(n + 1);
  while (usados.has(siguiente)) siguiente = formatearCodigoProducto(parseInt(siguiente, 10) + 1);
  return siguiente;
}
